import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest, getActionableApiErrorMessage } from "@/lib/queryClient";

const FEEDBACK_TYPES = [
  { value: "question", label: "Question" },
  { value: "bug", label: "Bug report" },
  { value: "suggestion", label: "Suggestion" },
  { value: "other", label: "Other" },
] as const;

type FeedbackType = (typeof FEEDBACK_TYPES)[number]["value"];

export default function FeedbackForm({
  screenContext,
  onSuccess,
}: {
  screenContext: string;
  onSuccess?: () => void;
}) {
  const [type, setType] = useState<FeedbackType>("question");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length < 10 || trimmed.length > 5000) {
      setError("Please enter between 10 and 5000 characters.");
      setSuccess(false);
      return;
    }
    setError("");
    setSuccess(false);
    setBusy(true);
    let submitted = false;
    try {
      await apiRequest("POST", "/api/feedback", {
        type,
        message: trimmed,
        platform: "web",
        screenContext,
      });
      setMessage("");
      setSuccess(true);
      submitted = true;
    } catch (cause) {
      setError(getActionableApiErrorMessage(
        cause,
        "We couldn't send your feedback. Please try again.",
      ));
    } finally {
      setBusy(false);
      if (submitted) onSuccess?.();
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <fieldset disabled={busy} className="space-y-2">
        <legend className="mb-2 text-sm font-medium">What is this about?</legend>
        <div className="flex flex-wrap gap-2">
          {FEEDBACK_TYPES.map((item) => (
            <Button
              key={item.value}
              type="button"
              size="sm"
              variant={type === item.value ? "default" : "outline"}
              onClick={() => setType(item.value)}
              aria-pressed={type === item.value}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </fieldset>
      <div className="space-y-2">
        <label htmlFor="feedback-message" className="text-sm font-medium">
          Your message
        </label>
        <Textarea
          id="feedback-message"
          value={message}
          onChange={(event) => {
            setMessage(event.target.value);
            setError("");
            setSuccess(false);
          }}
          placeholder="Describe your question, issue, or idea…"
          maxLength={5000}
          rows={5}
          required
          aria-describedby="feedback-count"
        />
        <div id="feedback-count" className="text-right text-xs text-muted-foreground">
          {message.length}/5000
        </div>
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {success && (
        <p role="status" className="text-sm text-green-600">
          Thank you. Your feedback was sent.
        </p>
      )}
      <Button type="submit" disabled={busy}>
        {busy ? "Sending…" : "Submit feedback"}
      </Button>
    </form>
  );
}